/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

import kotlin.math.min
import kotlin.math.pow

/**
 * Simple Moving Average forecasting algorithm
 * Calculates forecast by averaging the last N periods
 */
class SimpleMovingAverage(
    private val period: Int,
) : ForecastingAlgorithm {
    init {
        require(period > 0) { "Period must be positive" }
    }

    override fun forecast(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (data.isEmpty()) {
            return ForecastingResult(
                algorithmName = "Simple Moving Average",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("period" to period),
            )
        }

        if (data.size < period) {
            // Use all available data if insufficient for full period
            val average = data.average()
            return ForecastingResult(
                algorithmName = "Simple Moving Average",
                forecastValues = List(periods) { average },
                confidence = 0.5, // Lower confidence due to insufficient data
                historicalValues = listOf(average), // Single value representing the overall average
                parameters = mapOf("period" to period, "available_data" to data.size),
            )
        }

        // Calculate moving average from last 'period' values
        val lastValues = data.takeLast(period)
        val movingAverage = lastValues.average()

        // Simple MA mathematically produces constant forecast (no trend)
        val forecasts = List(periods) { movingAverage }

        // Calculate historical moving averages for trend line
        val historicalMAs = mutableListOf<Double>()
        for (i in (period - 1) until data.size) {
            val slice = data.subList(i - period + 1, i + 1)
            historicalMAs.add(slice.average())
        }

        // Calculate confidence based on data stability
        val variance = lastValues.map { (it - movingAverage).pow(2) }.average()
        val standardDeviation = kotlin.math.sqrt(variance)
        val meanValue = kotlin.math.abs(movingAverage)
        val coefficientOfVariation = if (meanValue > 0) standardDeviation / meanValue else 1.0
        val confidence = (1.0 / (1.0 + coefficientOfVariation)).coerceIn(0.0, 1.0)

        return ForecastingResult(
            algorithmName = "Simple Moving Average",
            forecastValues = forecasts,
            confidence = confidence,
            historicalValues = historicalMAs,
            parameters =
                mapOf(
                    "period" to period,
                    "last_average" to movingAverage,
                    "data_points_used" to lastValues.size,
                ),
        )
    }
}

/**
 * Exponential Weighted Moving Average
 * Gives more weight to recent observations
 */
class ExponentialWeightedMovingAverage(
    private val alpha: Double,
) : ForecastingAlgorithm {
    init {
        require(alpha in 0.0..1.0) { "Alpha must be between 0.0 and 1.0" }
    }

    override fun forecast(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (data.isEmpty()) {
            return ForecastingResult(
                algorithmName = "Exponential Weighted Moving Average",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("alpha" to alpha),
            )
        }

        if (data.size == 1) {
            return ForecastingResult(
                algorithmName = "Exponential Weighted Moving Average",
                forecastValues = List(periods) { data.first() },
                confidence = 0.3,
                parameters = mapOf("alpha" to alpha, "single_point" to true),
            )
        }

        // EXACT COPY of Android's calculateEWMA implementation
        val result = mutableListOf<Double>()
        // Start with simple average of first few points for stability (matching Android)
        val windowSize = minOf(3, data.size)
        var ewma = data.take(windowSize).average()

        // Start from windowSize onwards to match Android logic exactly
        for (i in windowSize until data.size) {
            ewma = alpha * data[i] + (1 - alpha) * ewma
            result.add(ewma)
        }

        // For forecasting: use last EWMA value (constant projection)
        val lastEWMA = if (result.isNotEmpty()) result.last() else ewma
        val forecasts = List(periods) { lastEWMA }

        // Confidence based on recent data consistency and alpha
        val recentData = data.takeLast(min(5, data.size))
        val recentMean = recentData.average()
        val recentVariance = recentData.map { (it - recentMean).pow(2) }.average()
        val recentStdDev = kotlin.math.sqrt(recentVariance)
        val consistency = if (recentMean != 0.0) 1.0 - (recentStdDev / kotlin.math.abs(recentMean)) else 0.5
        val confidence = (consistency * alpha).coerceIn(0.1, 0.9)

        return ForecastingResult(
            algorithmName = "Exponential Weighted Moving Average",
            forecastValues = forecasts,
            confidence = confidence,
            historicalValues = result, // Return the computed EWMA historical values
            parameters =
                mapOf(
                    "alpha" to alpha,
                    "final_ewma" to lastEWMA,
                    "data_points" to data.size,
                ),
        )
    }
}
